import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getRole, getRolePolicyBindings, bulkUpdatePolicyBindings } from '../services/iam';
import { IdCard, Shield, Save, X } from 'lucide-react';
import { toast } from '@/lib/toast';
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  ColumnDef,
} from '@tanstack/react-table';
import ResourceBindingsTab from '../components/ResourceBindingsTab';
import { Breadcrumbs, Button, Checkbox, ListPageSkeleton, PageHeader, Skeleton, Tabs, TanstackTable } from '@/components/ds-zta';
import { useUrlState } from '@/lib/useUrlState';
import { DetailLoadError, SectionLoadError } from '../components/DetailLoadError';
import { isNotFoundError, retryUnlessClientError } from '@/lib/apiError';

interface Module {
  name: string;
  displayName: string;
  operations: {
    key: string;
    label: string;
  }[];
}

const MODULES: Module[] = [
  {
    name: 'resources',
    displayName: 'Resources',
    operations: [
      { key: 'list', label: 'List/View All' },
      { key: 'get', label: 'View Details' },
      { key: 'create', label: 'Create' },
      { key: 'update', label: 'Update' },
      { key: 'delete', label: 'Delete' },
    ],
  },
  {
    name: 'access_requests',
    displayName: 'Access Requests',
    operations: [
      { key: 'list', label: 'List/View All' },
      { key: 'get', label: 'View Details' },
      { key: 'create', label: 'Create' },
      { key: 'approve', label: 'Approve' },
      { key: 'deny', label: 'Deny' },
      { key: 'duration', label: 'Update Duration' },
    ],
  },
  {
    name: 'users',
    displayName: 'Users',
    operations: [
      { key: 'list', label: 'List/View All' },
      { key: 'get', label: 'View Details' },
      { key: 'update_status', label: 'Update Status' },
      { key: 'delete', label: 'Delete' },
    ],
  },
  {
    name: 'roles',
    displayName: 'Roles',
    operations: [
      { key: 'list', label: 'List/View All' },
      { key: 'get', label: 'View Details' },
      { key: 'create', label: 'Create' },
      { key: 'update', label: 'Update' },
      { key: 'delete', label: 'Delete' },
    ],
  },
  {
    name: 'groups',
    displayName: 'Groups',
    operations: [
      { key: 'list', label: 'List/View All' },
      { key: 'get', label: 'View Details' },
      { key: 'create', label: 'Create' },
      { key: 'update', label: 'Update' },
      { key: 'delete', label: 'Delete' },
      { key: 'manage_members', label: 'Manage Members' },
    ],
  },
  {
    name: 'sessions',
    displayName: 'Sessions',
    operations: [
      { key: 'list', label: 'List/View All' },
      { key: 'get', label: 'View Details' },
      { key: 'terminate', label: 'Terminate' },
    ],
  },
  {
    name: 'access_logs',
    displayName: 'Access Logs',
    operations: [
      { key: 'list', label: 'List/View All' },
      { key: 'get', label: 'View Details' },
    ],
  },
  {
    name: 'policies',
    displayName: 'Policies',
    operations: [
      { key: 'list', label: 'List/View All' },
      { key: 'get', label: 'View Details' },
      { key: 'create', label: 'Create' },
      { key: 'update', label: 'Update' },
      { key: 'delete', label: 'Delete' },
    ],
  },
  {
    name: 'role_bindings',
    displayName: 'Role Bindings',
    operations: [
      { key: 'list', label: 'List/View All' },
      { key: 'create', label: 'Create' },
      { key: 'delete', label: 'Delete' },
    ],
  },
  {
    name: 'policy_bindings',
    displayName: 'Policy Bindings',
    operations: [
      { key: 'list', label: 'List/View All' },
      { key: 'create', label: 'Create' },
      { key: 'update', label: 'Update' },
      { key: 'delete', label: 'Delete' },
    ],
  },
  {
    name: 'terminal_sessions',
    displayName: 'Terminal Sessions',
    operations: [
      { key: 'list', label: 'List/View All' },
      { key: 'get', label: 'View Details' },
      { key: 'get_logs', label: 'View Logs' },
      { key: 'list_grouped', label: 'List Grouped' },
      { key: 'update_footage', label: 'Update Footage' },
    ],
  },
  {
    name: 'files',
    displayName: 'Files',
    operations: [
      { key: 'upload', label: 'Upload' },
      { key: 'download', label: 'Download' },
    ],
  },
];

interface ModuleRow extends Module {
  allSelected: boolean;
  someSelected: boolean;
}

const columnHelper = createColumnHelper<ModuleRow>();

const rolesCrumbs = (current: React.ReactNode) => (
  <Breadcrumbs
    items={[{ label: 'Identity' }, { label: 'Roles', href: '/roles' }, { label: current }]}
    renderLink={(item) => <Link to={item.href!} className="rc-breadcrumbs__link">{item.label}</Link>}
  />
);

const RoleDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // The open tab lives in the URL (design eval F24), so a link can open
  // Permissions directly and Back returns to the previous tab.
  const [url, setUrl] = useUrlState({ tab: 'resources' });
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());
  const [originalPermissions, setOriginalPermissions] = useState<Set<string>>(new Set());
  const [bindingIds, setBindingIds] = useState<Map<string, string>>(new Map());
  const [hasChanges, setHasChanges] = useState(false);

  const roleQuery = useQuery(['role', id], () => getRole(id!), { retry: retryUnlessClientError });
  const bindingsQuery = useQuery(['rolePolicyBindings', id], () => getRolePolicyBindings(id!), { retry: retryUnlessClientError });
  const { data: roleData, isLoading: isLoadingRole } = roleQuery;
  const { data: bindingsData, isLoading: isLoadingBindings } = bindingsQuery;

  const isLoading = isLoadingRole || isLoadingBindings;

  useEffect(() => {
    if (bindingsData?.data) {
      const permissions = new Set<string>();
      const ids = new Map<string, string>();
      
      bindingsData.data.forEach((binding: any) => {
        permissions.add(binding.policy_ref);
        ids.set(binding.policy_ref, binding.id);
      });
      
      setSelectedPermissions(permissions);
      setOriginalPermissions(new Set(permissions));
      setBindingIds(ids);
      setHasChanges(false);
    }
  }, [bindingsData]);

  const saveMutation = useMutation(
    async () => {
      // Calculate changes
      const toAdd = Array.from(selectedPermissions).filter(p => !originalPermissions.has(p));
      const toRemove = Array.from(originalPermissions).filter(p => !selectedPermissions.has(p));
      
      // Get binding IDs for permissions to remove
      const removeIds = toRemove
        .map(policyRef => bindingIds.get(policyRef))
        .filter((id): id is string => id !== undefined);

      // Call bulk update API
      const result = await bulkUpdatePolicyBindings(id!, {
        add: toAdd,
        remove: removeIds,
      });

      return result;
    },
    {
      onSuccess: (result) => {
        queryClient.invalidateQueries(['rolePolicyBindings', id]);
        toast.success(`Changes saved: ${result.added} added, ${result.removed} removed`);
        setHasChanges(false);
      },
      onError: (err: any) => {
        toast.error(`Failed to save changes: ${err.message}`);
      },
    }
  );

  const handleToggle = (policyRef: string) => {
    const newSelected = new Set(selectedPermissions);
    
    if (newSelected.has(policyRef)) {
      newSelected.delete(policyRef);
    } else {
      newSelected.add(policyRef);
    }
    
    setSelectedPermissions(newSelected);
    
    // Check if there are changes
    const hasChangesNow = 
      newSelected.size !== originalPermissions.size ||
      Array.from(newSelected).some(p => !originalPermissions.has(p));
    setHasChanges(hasChangesNow);
  };

  const handleSelectAll = (module: Module) => {
    const newSelected = new Set(selectedPermissions);
    const allSelected = module.operations.every(op => 
      newSelected.has(`${module.name}:${op.key}`)
    );

    module.operations.forEach(op => {
      const policyRef = `${module.name}:${op.key}`;
      if (allSelected) {
        newSelected.delete(policyRef);
      } else {
        newSelected.add(policyRef);
      }
    });

    setSelectedPermissions(newSelected);
    
    const hasChangesNow = 
      newSelected.size !== originalPermissions.size ||
      Array.from(newSelected).some(p => !originalPermissions.has(p));
    setHasChanges(hasChangesNow);
  };

  const handleSave = () => {
    saveMutation.mutate();
  };

  const handleCancel = () => {
    setSelectedPermissions(new Set(originalPermissions));
    setHasChanges(false);
  };

  // Prepare table data
  const tableData: ModuleRow[] = useMemo(() => {
    return MODULES.map(module => ({
      ...module,
      allSelected: module.operations.every(op => 
        selectedPermissions.has(`${module.name}:${op.key}`)
      ),
      someSelected: module.operations.some(op => 
        selectedPermissions.has(`${module.name}:${op.key}`)
      ),
    }));
  }, [selectedPermissions]);

  // Define columns
  const columns = useMemo<ColumnDef<ModuleRow, any>[]>(
    () => [
      columnHelper.display({
        id: 'module',
        header: 'Module',
        meta: { sortable: true, sortValue: (m: ModuleRow) => (m.displayName || m.name).toLowerCase() },
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-[var(--d2d-text-primary)]" />
            <span className="text-dense font-medium text-[var(--d2d-text-primary)]">
              {row.original.displayName}
            </span>
          </div>
        ),
      }),
      columnHelper.display({
        id: 'permissions',
        header: 'Permissions',
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-3">
            {row.original.operations.map((operation) => {
              const policyRef = `${row.original.name}:${operation.key}`;
              const isSelected = selectedPermissions.has(policyRef);

              return (
                <Checkbox
                  key={operation.key}
                  checked={isSelected}
                  onChange={() => handleToggle(policyRef)}
                  label={operation.label}
                />
              );
            })}
          </div>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className="flex items-start justify-center pt-1">
            <Button
              variant={row.original.allSelected ? 'ghost' : 'secondary'}
              size="sm"
              onClick={() => handleSelectAll(row.original)}
            >
              {row.original.allSelected ? 'Deselect All' : row.original.someSelected ? 'Select All' : 'Select All'}
            </Button>
          </div>
        ),
      }),
    ],
    [selectedPermissions]
  );

  // Create table instance
  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading || !roleData) {
    return (
      <div className="space-y-4">
        <PageHeader
          breadcrumbs={rolesCrumbs(isLoading ? 'Loading' : !roleQuery.error || isNotFoundError(roleQuery.error) ? 'Not found' : 'Error')}
          icon={<IdCard />}
          title={
            isLoading ? (
              <>
                <span className="sr-only">Loading role details</span>
                <Skeleton variant="text" width={220} />
              </>
            ) : (
              'Role'
            )
          }
        />
        {isLoading ? (
          <ListPageSkeleton filterCount={0} rowCount={6} />
        ) : (
          <DetailLoadError
            entity="role"
            error={roleQuery.error}
            icon={<IdCard />}
            onRetry={() => roleQuery.refetch()}
            backLabel="Back to roles"
            onBack={() => navigate('/roles')}
          />
        )}
      </div>
    );
  }

  const role = roleData;
  // Permissions exists only on a primary role; anything else opens Resources.
  const activeTab: 'permissions' | 'resources' = url.tab === 'permissions' && role?.is_primary ? 'permissions' : 'resources';

  return (
    <div className="space-y-4">
      {/* Header */}
      <PageHeader
        breadcrumbs={rolesCrumbs(role.name)}
        icon={<IdCard />}
        title={role.name}
        description={role.description}
        secondaryActions={
          hasChanges ? (
            <Button
              variant="secondary"
              leadingIcon={<X className="h-4 w-4" />}
              onClick={handleCancel}
              disabled={saveMutation.isLoading}
            >
              Cancel
            </Button>
          ) : undefined
        }
        primaryAction={
          hasChanges ? (
            <Button
              leadingIcon={<Save className="h-4 w-4" />}
              onClick={handleSave}
              disabled={saveMutation.isLoading}
              loading={saveMutation.isLoading}
            >
              {saveMutation.isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          ) : undefined
        }
      />

      {/* Tabs */}
      <Tabs
        value={activeTab}
        urlParam="tab"
        onChange={(v) => setUrl({ tab: v })}
        aria-label="Role sections"
      >
        <Tabs.List>
          <Tabs.Tab value="resources">Resources</Tabs.Tab>
          {role?.is_primary && <Tabs.Tab value="permissions">Permissions</Tabs.Tab>}
        </Tabs.List>
      </Tabs>

      {/* Tab Content */}
      {activeTab === 'permissions' && role?.is_primary && (
        <div className="bg-[var(--d2d-bg-card)] shadow-sm rounded-lg border border-[var(--d2d-border)] overflow-hidden">
        <div className="px-6 py-4 border-b border-[var(--d2d-border)]">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-medium text-[var(--d2d-text-primary)]">Permissions</h2>
              <p className="text-sm text-[var(--d2d-text-muted)] mt-1">
                {selectedPermissions.size} permission(s) selected
                {hasChanges && <span className="ml-2 text-[var(--d2d-warning)]">• Unsaved changes</span>}
              </p>
            </div>
          </div>
        </div>

        {bindingsQuery.isError ? (
          // Without the current bindings every box would read unticked, and a
          // save would compute its diff against nothing.
          <div className="p-4">
            <SectionLoadError what="this role's permissions" error={bindingsQuery.error} onRetry={() => bindingsQuery.refetch()} />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <TanstackTable aria-label="Module permissions" table={table} loading={isLoading} />
          </div>
        )}
      </div>
      )}

      {activeTab === 'resources' && <ResourceBindingsTab roleId={id!} />}
    </div>
  );
};

export default RoleDetailPage;
